import { memo, useMemo, useState, useEffect, useCallback } from 'react';
import {
    Box,
    Stack,
    TextField,
    FormControlLabel,
    Switch,
    Slider,
    Button,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { ComponentBlockCustom } from 'src/sections/mui/component-block-custom';
import {
    setFilterAveragePositionPLA,
    setFilterBrandPLA,
    setFilterPricingRangePLA,
    setNotificationBrandInfringement,
} from 'src/context/reducers/filter-settings/filterSlice';

type Props = {
    hasPla: boolean;
};

function AdsFilters({ hasPla }: Props) {
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);
    const workspace: any = useCurrentWorkspace();

    const [localAveragePosition, setLocalAveragePosition] = useState<[number, number]>(
        () => (filterStore?.averagePositionPLA as [number, number]) || [0, 50]
    );

    useEffect(() => {
        const valueFromStore =
            (filterStore?.averagePositionPLA as [number, number]) || [0, 50];
        setLocalAveragePosition(valueFromStore);
    }, [filterStore?.averagePositionPLA]);

    const handlePositionChange = useCallback(
        (_e: any, newValue: number | number[]) => {
            setLocalAveragePosition(newValue as [number, number]);
            dispatch(setFilterAveragePositionPLA(newValue as [number, number]));
        },
        [dispatch]
    );

    const positionMarks = useMemo(
        () =>
            Array.from({ length: 11 }, (_, i) => ({
                value: i * 10,
                label: `${i * 10}`,
            })),
        []
    );

    const pricingFrom = filterStore?.pricingRangePLA?.[0] ?? 0;
    const pricingTo = filterStore?.pricingRangePLA?.[1] ?? 50000;

    const [pricingFromLocal, setPricingFromLocal] = useState(pricingFrom);
    const [pricingToLocal, setPricingToLocal] = useState(pricingTo);

    useEffect(() => {
        setPricingFromLocal(pricingFrom);
        setPricingToLocal(pricingTo);
    }, [pricingFrom, pricingTo]);

    const commitPricingRange = useCallback(() => {
        dispatch(
            setFilterPricingRangePLA([
                Number.isFinite(pricingFromLocal) ? pricingFromLocal : 0,
                Number.isFinite(pricingToLocal) ? pricingToLocal : 50000,
            ])
        );
    }, [dispatch, pricingFromLocal, pricingToLocal]);

    const handleBrandInfringementChange = (event: any) => {
        const checked = event.target.checked;

        if (checked) {
            dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
        } else {
            dispatch(setFilterBrandPLA([]));
            dispatch(setNotificationBrandInfringement(true));
        }
    };

    const handleResetFilters = useCallback(() => {
        dispatch(setFilterBrandPLA([]));
        dispatch(setFilterPricingRangePLA([0, 50000]));
        dispatch(setFilterAveragePositionPLA([0, 50]));
        dispatch(setNotificationBrandInfringement(false));

        setPricingFromLocal(0);
        setPricingToLocal(50000);
        setLocalAveragePosition([0, 50]);
    }, [dispatch]);

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                height: '100%',
            }}
        >
            {workspace?.showPlaAlert && (
                <>
                    <Box px={"12px"} position={"relative"} top={3}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1" height="50" viewBox="0 0 1 21" fill="none">
                            <path d="M0.5 0.5V20.5" stroke="#919EAB" stroke-opacity="0.5" stroke-linecap="round" />
                        </svg>
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={filterStore?.brandsPLA?.length !== 0}
                                onChange={handleBrandInfringementChange}
                            />
                        }
                        label="Brand Infringement"
                    />
                </>
            )}

            <Box px={"12px"} position={"relative"} top={3}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1" height="50" viewBox="0 0 1 21" fill="none">
                    <path d="M0.5 0.5V20.5" stroke="#919EAB" stroke-opacity="0.5" stroke-linecap="round" />
                </svg>
            </Box>


            {hasPla &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                        <Typography
                            sx={{
                                color: '#333C47',
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '120%',
                            }}
                        >
                            Price range
                        </Typography>
                    </Box>

                    <TextField
                        size="small"
                        label="From"
                        type="number"
                        value={pricingFromLocal}
                        onChange={(e) => setPricingFromLocal(Number(e.target.value))}
                        onBlur={commitPricingRange}
                        onKeyDown={(e) => e.key === 'Enter' && commitPricingRange()}
                        sx={{
                            width: 110,
                            '& .MuiInputLabel-root': {
                                color: '#919EAB',
                                fontSize: '13px'
                            },
                            '& .MuiInputBase-input': {
                                color: '#637381',
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                            },
                        }}
                    />

                    <TextField
                        size="small"
                        label="To"
                        type="number"
                        value={pricingToLocal}
                        onChange={(e) => setPricingToLocal(Number(e.target.value))}
                        onBlur={commitPricingRange}
                        onKeyDown={(e) => e.key === 'Enter' && commitPricingRange()}
                        sx={{
                            width: 110,
                            '& .MuiInputLabel-root': {
                                color: '#919EAB',
                                fontSize: '13px'
                            },
                            '& .MuiInputBase-input': {
                                color: '#637381',
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                            },
                        }}
                    />

                    <Box px={"12px"} position={"relative"} top={3}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1" height="50" viewBox="0 0 1 21" fill="none">
                            <path d="M0.5 0.5V20.5" stroke="#919EAB" stroke-opacity="0.5" stroke-linecap="round" />
                        </svg>
                    </Box>
                </Box>
            }


            <Typography
                sx={{
                    color: '#333C47',
                    fontFamily: '"Public Sans", sans-serif',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    mr: "8px",
                    lineHeight: '120%',
                }}
            >
                Position
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 260,
                    height: 40,
                    mr: '12px',
                    px: 1.5,
                    border: '1px solid #DCE3EA',
                    borderRadius: '8px',
                    backgroundColor: '#FFF',
                    gap: 1,
                }}
            >
                <Typography
                    sx={{
                        color: '#333C47',
                        fontFamily: '"Public Sans", sans-serif',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '23px',
                        width: 16,
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                >
                    {localAveragePosition[0]}
                </Typography>

                <Box px={'10px'} width={'100%'}>
                    <Slider
                        value={localAveragePosition}
                        onChange={handlePositionChange}
                        min={0}
                        max={20}
                        step={1}
                        disableSwap
                        valueLabelDisplay="off"
                        sx={{
                            flex: 1,
                            color: '#1877F2',

                            '& .MuiSlider-rail': {
                                height: 4,
                                borderRadius: 999,
                                backgroundColor: '#E5EAF0',
                                opacity: 1,
                            },

                            '& .MuiSlider-track': {
                                height: 4,
                                borderRadius: 999,
                                border: 'none',
                            },

                            '& .MuiSlider-thumb': {
                                width: 18,
                                height: 18,
                                bgcolor: '#FFF',
                                border: '1px solid #DCE3EA',
                                boxShadow: '0px 2px 6px rgba(0,0,0,.16)',

                                '&::before': {
                                    display: 'none',
                                },
                            },

                            '& .MuiSlider-valueLabel': {
                                backgroundColor: '#202B3C',
                                borderRadius: '8px',

                                '&::before': {
                                    backgroundColor: '#202B3C',
                                },
                            },
                        }}
                    />
                </Box>

                <Typography
                    sx={{
                        color: '#333C47',
                        fontFamily: '"Public Sans", sans-serif',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '23px',
                        width: 20,
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                >
                    {localAveragePosition[1]}
                </Typography>
            </Box>

            <Button
                variant="outlined"
                color="inherit"
                onClick={handleResetFilters}
                sx={{
                    width: 35,
                    height: 35,
                    minWidth: 35,
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                >
                    <path
                        d="M16.5 7.5C16.5 7.5 14.9963 5.45116 13.7746 4.22868C12.553 3.0062 10.8648 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75C12.0773 15.75 14.6737 13.6907 15.4862 10.875M12 7.5H16.5V3"
                        stroke="#5E6D7B"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Button>
        </Stack>
    );
}

export default memo(AdsFilters);